import axios from 'axios';
import { getApiBaseUrl } from '../../services/api';
import type { ChipTone } from '../../components/aa';

export type ViewerKind = 'partner' | 'provider_owner' | 'owner' | 'champion';
export type Lane = 'open' | 'waiting' | 'approved' | 'work' | 'declined';

export interface Person {
  email: string;
  name: string;
  role: string;
}

export interface Stats {
  opportunities: number;
  decide: number;
  asked: number;
  answered: number;
  sessions_open: number;
  sessions: number;
  scoped: number;
  running: number;
  projects: number;
  people: number;
  onboarded: number;
  tools_used: number;
  tools_connected: number;
}

export interface Opportunity {
  id: string;
  kind: 'problem' | 'request' | 'tool';
  title: string;
  statement: string;
  lane: Lane;
  source: string;
  created_by: Person | null;
  department: string;
  created_at: string | null;
  updated_at: string | null;
  request_id?: string | null;
}

export interface SessionPersonRow {
  email: string;
  name: string;
  role: string;
  answered_at: string | null;
  onboarded: boolean;
}

export interface SessionRow {
  id: string;
  title: string;
  kind: string;
  sent_at: string | null;
  sender: string;
  recipients: number;
  answered: number;
  people: SessionPersonRow[];
}

export interface CompanyPerson {
  email: string;
  name: string;
  role: string;
  department: string;
  onboarded: boolean;
  sessions_answered: number;
}

export interface ToolConnection {
  scope: string;
  by: Person | null;
}

export interface Tool {
  key: string;
  label: string;
  logo: string;
  said_by: string[];
  catalog: boolean;
  connected: boolean;
  connections: ToolConnection[];
  people: number;
  added_by: Person | null;
}

export interface Work {
  projects: { id: string; title: string; status: string }[];
  routines: { id: string; title: string; created_by: Person | null; cadence: string; running: boolean; last_run_at: string | null }[];
  workflows: { id: string; title: string; channel: string; running: boolean; updated_at: string | null }[];
  skills: { id: string; title: string; created_by: Person | null; published: boolean; scope: string; kind: string }[];
}

export interface CompanyView {
  id: string;
  name: string;
  initials: string;
  own: boolean;
  can_send: boolean;
  stats: Stats;
  opportunities: Opportunity[];
  sessions: SessionRow[];
  people?: CompanyPerson[];
  work?: Work;
  tools?: { connected: Tool[]; not_connected: Tool[]; unreachable: Tool[] };
}

export interface Viewer {
  kind: ViewerKind;
  has_clients: boolean;
}

export interface AdoptionOverview {
  viewer: Viewer;
  companies: CompanyView[];
}

const base = () => `${getApiBaseUrl()}/aai`;

export interface GraphNode {
  key: string;
  kind: string;
  label: string;
  props: Record<string, unknown>;
  created_by: string | null;
}

export interface GraphEdge {
  src: string;
  rel: string;
  dst: string;
  props: Record<string, unknown>;
}

export interface RelatedNode {
  key: string;
  kind: string;
  label: string;
  direct: boolean;
}

export interface CompanyGraph {
  related: RelatedNode[];
  built_at: string | null;
  counts: Record<string, number>;
  links: number;
  brief: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export const adoptionApi = {
  overview: () => axios.get<AdoptionOverview>(`${base()}/adoption`),
  graph: (companyId: string, focus = '') =>
    axios.get<CompanyGraph>(`${base()}/adoption/companies/${encodeURIComponent(companyId)}/graph`, { params: { focus } }),
  rebuildGraph: (companyId: string) =>
    axios.post<CompanyGraph>(`${base()}/adoption/companies/${encodeURIComponent(companyId)}/graph/rebuild`),
  company: (companyId: string) =>
    axios.get<{ viewer: Viewer; company: CompanyView }>(`${base()}/adoption/companies/${encodeURIComponent(companyId)}`),
};

export const isClientView = (viewer: Viewer): boolean => viewer.kind === 'owner' || viewer.kind === 'champion';

const EMPTY: Stats = {
  opportunities: 0, decide: 0, asked: 0, answered: 0, sessions_open: 0, sessions: 0, scoped: 0,
  running: 0, projects: 0, people: 0, onboarded: 0, tools_used: 0, tools_connected: 0,
};

export const sumStats = (companies: CompanyView[]): Stats => {
  const total: Stats = { ...EMPTY };
  companies.forEach((c) => {
    (Object.keys(total) as (keyof Stats)[]).forEach((k) => { total[k] += c.stats[k] || 0; });
  });
  return total;
};

export const percent = (part: number, whole: number): number => {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
};

export const plural = (n: number, one: string, many: string): string => {
  if (n === 1) return `${n} ${one}`;
  return `${n} ${many}`;
};

export const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const LANE_TONE: Record<Lane, ChipTone> = {
  open: 'warn',
  waiting: 'run',
  approved: 'ok',
  work: 'ok',
  declined: 'neutral',
};

export const laneLabel = (lane: Lane, clientView: boolean): string => {
  if (lane === 'open' && clientView) return 'New idea';
  if (lane === 'open') return 'Not proposed yet';
  if (lane === 'waiting' && clientView) return 'Waiting on your decision';
  if (lane === 'waiting') return 'Waiting on the client';
  if (lane === 'approved') return 'Approved';
  if (lane === 'work') return 'Became work';
  return 'Declined';
};

export const LANES: Lane[] = ['open', 'waiting', 'approved', 'work', 'declined'];

export const PROJECT_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  PENDING: { label: 'Waiting for an expert', tone: 'neutral' },
  ASSIGNED: { label: 'Expert assigned', tone: 'run' },
  ACCEPTED: { label: 'Expert assigned', tone: 'run' },
  IN_PROGRESS: { label: 'Being built', tone: 'run' },
  SUBMITTED: { label: 'In review', tone: 'run' },
  AI_REVIEW: { label: 'In review', tone: 'run' },
  CUSTOMER_REVIEW: { label: 'Waiting on the client', tone: 'warn' },
  COMPLETED: { label: 'Live', tone: 'ok' },
  DISPUTED: { label: 'Disputed', tone: 'err' },
};

export const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
};
